﻿import { faAdd, faCheckSquare, faSpinner, faSubtract } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { JSX, memo, useRef, useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Autocomplete, Checkbox, FormControlLabel } from "@mui/material";
import TextField from "@mui/material/TextField";
import { faSquare } from "@fortawesome/free-regular-svg-icons";

import { NumberField } from "../NumberField";
import { CellType, TableCellRenderHelpers, TableRow, TableValue, TypedTableValue } from "./DataTable";
import ButtonDatePicker from "../Buttons/ButtonFieldDatePicker";
import camelToCapitalized from "../../utilities/helpers/camelCaseToCapitalized";
import { formatFixed, formatNumber, formatPlain, withPercentSign } from "../../utilities/format";
import { reconcileDrafts, withoutDraft } from "./draftReconciliation";

/** Pages get each cell typed by its `type`; the row drives every type the same way. */
function untyped<IDType>(v: TableValue<IDType>) {
  return v as unknown as TypedTableValue<IDType, CellType>;
}

type BuiltInCell<IDType> = Exclude<TableValue<IDType>, { type: "custom" }>;

interface TableRowElementProps<IDType extends number | string = number> {
  tableRow: TableRow<IDType>;
  isLoading: boolean;
  showDetailsColumnHeadCell: boolean;
  expandedRowRenderer?: (row: TableRow<IDType>) => React.ReactNode;
  rowTailwindClasses?: string;
  rowContainerTailwindClasses?: string;
  lockedTabKey?: string | null;
  registerTabInputRef?: (rowId: IDType, tabLockKey: string, el: HTMLElement | null) => void;
  focusNextLockedCell?: (rowId: IDType, tabLockKey: string) => void;
  /** Reports whether this row currently holds unsaved edit drafts, so the table
   *  can warn the user if the row is removed mid-edit by a live update. */
  onDraftStateChange?: (rowId: IDType, hasDrafts: boolean) => void;
  /** Disable the per-row fade-in. Used by the virtualized list, where rows
   *  mount/unmount on every scroll position and a fade would look like flicker. */
  disableMountAnimation?: boolean;
}

function TableRowElementComponent<IDType extends number | string = number>(
  {
    tableRow,
    isLoading,
    showDetailsColumnHeadCell,
    expandedRowRenderer,
    rowTailwindClasses,
    rowContainerTailwindClasses,
    lockedTabKey,
    registerTabInputRef,
    focusNextLockedCell,
    onDraftStateChange,
    disableMountAnimation,
  }: TableRowElementProps<IDType>
) {
  const focusValueRef = useRef<Record<string, unknown>>({});
  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [draftValues, setDraftValues] = useState<Record<string, unknown>>({});
  const [prevTableRow, setPrevTableRow] = useState(tableRow);

  if (tableRow !== prevTableRow) {
    setPrevTableRow(tableRow);
    setDraftValues(prev => reconcileDrafts(prev, tableRow.values));
  }

  const setDraft = useCallback((cellName: string, val: unknown) => {
    setDraftValues(prev => ({ ...prev, [cellName]: val }));
  }, []);

  const clearDraft = useCallback((cellName: string) => {
    setDraftValues(prev => withoutDraft(prev, cellName));
  }, []);

  const getDisplayValue = <V extends TableValue<IDType>>(v: V) =>
    (v.name in draftValues ? draftValues[v.name] : v.value) as V["value"];
  const draftedRow: TableRow<IDType> = Object.keys(draftValues).length === 0
    ? tableRow
    : {
      ...tableRow,
      values: tableRow.values.map(cv =>
        cv.name in draftValues ? { ...cv, value: draftValues[cv.name] } as TableValue<IDType> : cv
      ),
    };

  // Note: This is to report 'dirty' state up so the table can warn if this row is removed mid-edit.
  // Deliberately no unmount cleanup: a row deleted while dirty must stay "dirty"
  // long enough for the table to notice it vanished.
  useEffect(() => {
    onDraftStateChange?.(tableRow.id, Object.keys(draftValues).length > 0);
  }, [draftValues, tableRow.id, onDraftStateChange]);

  const rowBorderClasses = [
    "border-b", "border-theme-table-border",
    "transition-colors",
    rowTailwindClasses ?? ""
  ];

  const getCellFocusKey = (cellName: string) => `${String(tableRow.id)}::${cellName}`;

  const scheduleDebounced = (v: typeof tableRow.values[number], newValue: unknown) => {
    const timerKey = getCellFocusKey(v.name);
    clearTimeout(debounceTimersRef.current[timerKey]);
    debounceTimersRef.current[timerKey] = setTimeout(async () => {
      delete debounceTimersRef.current[timerKey];
      setIsWorking(true);
      await untyped(v).onChangeDebounced!(draftedRow, newValue);
      clearDraft(v.name);
      setIsWorking(false);
    }, v.onChangeDebouncedMs ?? 600);
  };

  const changeCell = (v: typeof tableRow.values[number], newValue: unknown) => {
    setDraft(v.name, newValue);
    untyped(v).onChange?.(draftedRow, newValue);
    if (v.onChangeDebounced) scheduleDebounced(v, newValue);
  };

  const toggleExpanded = async () => setIsExpanded(prev => !prev);

  const getCellHelpers = (v: typeof tableRow.values[number]): TableCellRenderHelpers => {
    const isTabLocked = !!lockedTabKey && !!v.tabLockKey && lockedTabKey === v.tabLockKey;
    const resolveCell = (cellName?: string) =>
      cellName ? tableRow.values.find(x => x.name === cellName) : v;
    return {
      change: (newValue) => changeCell(v, newValue),
      tabLock: {
        isTabLocked,
        registerInputRef: (el) => {
          if (!v.tabLockKey || !registerTabInputRef) return;
          registerTabInputRef(tableRow.id, v.tabLockKey, el);
        },
        handleLockedTabbing: (e) => {
          if (!isTabLocked || !focusNextLockedCell) return;
          if (e.key !== "Tab" || e.shiftKey) return;
          e.preventDefault();
          setTimeout(() => focusNextLockedCell(tableRow.id, v.tabLockKey!), 0);
        },
      },
      row: {
        isSaving: isWorking,
        setIsSaving: setIsWorking,
      },
      draft: {
        get: (cellName?: string) => {
          const target = resolveCell(cellName);
          if (!target) return undefined;
          return target.name in draftValues ? draftValues[target.name] : target.value;
        },
        set: (newValue: unknown, cellName?: string) => {
          const target = resolveCell(cellName);
          if (!target) return;
          setDraft(target.name, newValue);
        },
        clear: (cellName) => {
          const target = resolveCell(cellName);
          if (!target) return;
          clearDraft(target.name);
        },
      },
    };
  };

  function renderEditableCell(v: BuiltInCell<IDType>, _i: number) {
    const helpers = getCellHelpers(v);
    const handleChange = (newValue: unknown) => changeCell(v, newValue);

    const handleBlur = async (newValue: unknown, initialValue: unknown) => {
      if (newValue === initialValue) return;
      setIsWorking(true);
      await untyped(v).onBlur!(draftedRow, newValue);
      clearDraft(v.name);
      setIsWorking(false);
    };

    switch (v.type) {
      case 'string':
      case 'email':
        return (
          <div className="relative w-full">
            <TextField
              disabled={(v.disabled ?? false) || isWorking}
              value={getDisplayValue(v) ?? ''}
              size="small"
              type={v.type}
              fullWidth
              onFocus={(e) => {
                focusValueRef.current[getCellFocusKey(v.name)] = e.target.value;
              }}
              onChange={(e) => handleChange(e.target.value)}
              {...(v.onBlur && {
                onBlur: async (e) => {
                  const key = getCellFocusKey(v.name);
                  const initialValue = focusValueRef.current[key];
                  delete focusValueRef.current[key];
                  await handleBlur(e.target.value, initialValue);
                },
              })}
              inputRef={helpers.tabLock.registerInputRef}
              onKeyDown={helpers.tabLock.handleLockedTabbing}
            />
            {isWorking && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2">
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            )}
          </div>
        );

      case 'integer':
        return (
          <NumberField.Root
            disabled={v.disabled || isWorking}
            format={{ style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }}
            value={getDisplayValue(v)}
            className="h-full bg-theme-surface-2"
            onValueChange={(val) => {
              focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = val;
              handleChange(val);
            }}
          >
            <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
              <NumberField.Decrement />
              <NumberField.Input
                className="h-full w-full px-1 py-2 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition"
                onFocus={() => {
                  focusValueRef.current[getCellFocusKey(v.name)] = v.value;
                  focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = v.value;
                }}
                {...(v.onBlur && {
                  onBlur: async () => {
                    const key = getCellFocusKey(v.name);
                    const initialValue = focusValueRef.current[key];
                    const parsedValue = focusValueRef.current[`${key}::parsed`] ?? initialValue;
                    delete focusValueRef.current[key];
                    delete focusValueRef.current[`${key}::parsed`];
                    await handleBlur(parsedValue, initialValue);
                  },
                })}
              />
              {isWorking && (
                <span className="absolute top-1/2 right-1/2 -translate-y-1/2 translate-x-1/2">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </span>
              )}
              <NumberField.Increment />
            </NumberField.Group>
          </NumberField.Root>
        );

      case 'decimal':
        return (
          <NumberField.Root
            disabled={v.disabled || isWorking}
            value={getDisplayValue(v)}
            format={{ style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2 }}
            className="h-full bg-theme-surface-2"
            onValueChange={(val) => {
              focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = val;
              handleChange(val);
            }}
          >
            <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
              <NumberField.Input
                className="h-full w-full px-1 py-1 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition"
                onFocus={() => {
                  focusValueRef.current[getCellFocusKey(v.name)] = v.value;
                  focusValueRef.current[`${getCellFocusKey(v.name)}::parsed`] = v.value;
                }}
                {...(v.onBlur && {
                  onBlur: async () => {
                    const key = getCellFocusKey(v.name);
                    const initialValue = focusValueRef.current[key];
                    const parsedValue = focusValueRef.current[`${key}::parsed`] ?? initialValue;
                    delete focusValueRef.current[key];
                    delete focusValueRef.current[`${key}::parsed`];
                    await handleBlur(parsedValue, initialValue);
                  },
                })}
              />
              {isWorking && (
                <span className="absolute top-2 right-2">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </span>
              )}
            </NumberField.Group>
          </NumberField.Root>
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Checkbox
                disabled={v.disabled}
                checked={!!getDisplayValue(v)}
                onChange={e => handleChange(e.target.checked)}
                slotProps={{ input: { "aria-label": v.label ?? v.name } }}
                style={{ padding: '2px 9px 6px' }}
                className="!text-theme-text"
                size="small"
                ref={helpers.tabLock.registerInputRef}
                onKeyDown={helpers.tabLock.handleLockedTabbing}
              />
            }
            slotProps={{ typography: { className: '!text-theme-text' } }}
            label=""
            labelPlacement="top"
            sx={{ margin: 0 }}
          />
        );

      case 'date':
        return (
          <ButtonDatePicker
            disabled={v.disabled}
            className="w-full"
            value={getDisplayValue(v) ?? null}
            onChange={e => handleChange(e)}
            slotProps={{ field: { "aria-label": v.label ?? v.name } }}
            inputRef={helpers.tabLock.registerInputRef}
          />
        );

      case 'select':
        return (
          <Autocomplete
            classes={{ root: 'w-full' }}
            size="small"
            disabled={isWorking || v.disabled}
            value={v.options?.find((opt) => opt.value === getDisplayValue(v)) ?? null}
            options={v.options ?? []}
            onChange={async (_, val) => {
              const next = val?.value ?? null;
              setDraft(v.name, next);
              if (v.onChange) {
                setIsWorking(true);
                await v.onChange(tableRow, next);
                setIsWorking(false);
                clearDraft(v.name);
              }
              if (v.onChangeDebounced) scheduleDebounced(v, next);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={helpers.tabLock.registerInputRef}
                onKeyDown={helpers.tabLock.handleLockedTabbing}
                label={v.label ?? v.name}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {isWorking && <FontAwesomeIcon icon={faSpinner} spin />}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            getOptionLabel={(option) => camelToCapitalized(option.label) ?? ''}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>{camelToCapitalized(option.label)}</li>
            )}
          />
        );

      default:
        return <span>{v.value}</span>;
    }
  }

  function renderReadOnlyCell(v: BuiltInCell<IDType>) {
    switch (v.type) {
      // Note: a nullable number column reaches here as null, and formatting it unguarded threw out
      // of the map above, so one empty cell took the whole row with it rather than rendering blank.
      case 'percentage': return <span>{v.value == null ? '' : withPercentSign(formatNumber(v.value, { maximumFractionDigits: 2 }))}</span>;
      case 'decimal': return <span>{typeof v.value === 'number' ? formatFixed(v.value, 2) : ''}</span>;
      case 'boolean': return <span>{v.value
        ? <FontAwesomeIcon icon={faCheckSquare} className="text-theme-text-muted text-lg rounded px-0.5" />
        : <FontAwesomeIcon icon={faSquare} className="text-theme-text-muted text-lg rounded px-0.5" />}
      </span>;
      case 'date': return <span>{v.value?.toISODate()}</span>;
      case 'select': return <span>{camelToCapitalized(v.options?.find((opt) => +opt.value === Number(v.value))?.label ?? v.value ?? '')}</span>;
      default: return <span>{typeof v.value === 'number' ? formatPlain(v.value) : v.value}</span>;
    }
  }

  return isLoading ? (
    <div className="fade-up"><FontAwesomeIcon icon={faSpinner} spin /></div>
  ) : (
    <div
      className={`line ${disableMountAnimation ? '' : 'animate-fade-in'} ${rowContainerTailwindClasses ?? ''}`}
      id={`row-${tableRow.id}`}
    >
      <ul className={rowBorderClasses.filter(Boolean).join(" ")}>
        {draftedRow.values.map((v, i) => (
          <li
            key={i}
            className={`flex items-center text-theme-table-row-contrast justify-center text-[0.9rem] w-full transition ${v.tailwindClasses ?? ''} ${lockedTabKey && v.tabLockKey === lockedTabKey
              ? "bg-theme-warning border-x outline-2 outline-y outline-theme-warning border-theme-warning"
              : ""
              }`}
          >
            {v.type === "custom" || v.render
              ? untyped(v).render!(untyped(v), draftedRow, getCellHelpers(v))
              : v.editable
                ? renderEditableCell(v, i)
                : renderReadOnlyCell(v)
            }
          </li>
        ))}

        {showDetailsColumnHeadCell && (
          <li
            className="cursor-pointer text-center flex items-center justify-center text-[0.9rem] w-full basis-[5%]"
            onClick={toggleExpanded}
          >
            <i className="font-bold text-xl relative text-theme-table-row-contrast">
              <span className={`transition ease-in-out ${isExpanded ? 'opacity-0' : 'opacity-100'}`}>
                <FontAwesomeIcon icon={faAdd} />
              </span>
              <span className={`absolute inset-0 transition ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}>
                <FontAwesomeIcon icon={faSubtract} />
              </span>
            </i>
          </li>
        )}
      </ul>

      {expandedRowRenderer && (
        <AnimatePresence mode="sync">
          <motion.div
            key={`${isExpanded}`}
            className={isExpanded ? "border-y border-theme-table-border bg-theme-table-row-surface" : ""}
            initial={{ height: '0' }}
            animate={{ height: isExpanded ? 'auto' : 0 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.10, ease: 'easeIn' }}
          >
            {isExpanded && expandedRowRenderer(tableRow)}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

export const TableRowElement = memo(
  TableRowElementComponent
) as <IDType extends number | string = number>(
  props: TableRowElementProps<IDType>
) => JSX.Element;